import React, { useState, useEffect, useCallback, useMemo } from 'react';
import SmartTable, { SmartTableColumn } from '../../../shared/components/SmartTable';
import SmartTableButtons from '../../../shared/components/SmartTableButtons';
import LayoutWrapper from '../../../shared/components/LayoutWrapper';
import SearchableDropdown from '../../../shared/components/SearchableDropdown';
import { useAuth } from '../../../shared/hooks/useAuth';
import api from '../../../shared/utils/api';
import ErrorPopup from '../../../shared/components/ErrorPopup';
import WarningPopup from '../../../shared/components/WarningPopup';
import TemplateLedgerReceiptItemPopup from './TemplateLedgerReceiptItemPopup';

interface TemplateLedger {
  Oid: string;
  Note: string | null;
  LedgerTemplateType: number | null;
  ObjectType: number | null;
  BusinessClassName: string | null;
}

interface TemplateLedgerReceiptItem {
  Oid: string;
  LedgerTemplateReceiptId: string;
  BusinessClassOid: string | null;
  CustomerOid: string | null;
  BusinessCustomerOid: string | null;
  Description: string | null;
  ToAmount: number | null;
  DebitAccountId: string | null;
  CreditAccountId: string | null;
  VATAccountId: string | null;
  CashflowCategoryId: string | null;
  CityTaxAccountId: string | null;
  DebitAccountCode: string | null;
  DebitAccountName: string | null;
  CreditAccountCode: string | null;
  CreditAccountName: string | null;
  VATAccountCode: string | null;
  VATAccountName: string | null;
  CityTaxAccountCode: string | null;
  CityTaxAccountName: string | null;
  CashflowCategoryName: string | null;
  BusinessClassName: string | null;
}

const formatNumber = (value: any) => {
  if (value === null || value === undefined) return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return '-';
  return num.toLocaleString('mn-MN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const TemplateLedgerPage: React.FC = () => {
  const { userAuth } = useAuth();
  const [receiptItems, setReceiptItems] = useState<TemplateLedgerReceiptItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showWarningAlert, setShowWarningAlert] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [selectedTemplateLedgerOid, setSelectedTemplateLedgerOid] = useState<string | null>(null);
  const [showColumnSearch, setShowColumnSearch] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [selectedBusinessClassName, setSelectedBusinessClassName] = useState<string>('');
  const [selectedNote, setSelectedNote] = useState<string>('');
  const [allTemplateLedgerData, setAllTemplateLedgerData] = useState<TemplateLedger[]>([]);
  const [allBusinessClasses, setAllBusinessClasses] = useState<Array<{ Oid: string; BusinessClassName: string; Name: string }>>([]);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedItem, setSelectedItem] = useState<TemplateLedgerReceiptItem | null>(null);

  // Fetch TemplateLedger data (only for filter options)
  const fetchTemplateLedger = useCallback(async () => {
    try {
      const result = await api.get('/api/template-ledger');
      
      if (result && result.success && result.data) {
        setAllTemplateLedgerData(result.data);
      } else {
        setErrorMessage(result.message || 'TemplateLedger мэдээлэл авахад алдаа гарлаа');
        setShowErrorPopup(true);
        setAllTemplateLedgerData([]);
      }
    } catch (error: any) {
      console.error('❌ Error fetching template ledger:', error);
      setErrorMessage(error.message || 'TemplateLedger мэдээлэл авахад алдаа гарлаа');
      setShowErrorPopup(true);
      setAllTemplateLedgerData([]);
    }
  }, []);

  // Fetch all BusinessClass data
  const fetchBusinessClasses = useCallback(async () => {
    console.log('🔍 Fetching BusinessClass data...');
    try {
      const result = await api.get('/api/BusinessClassOid');
      console.log('📡 BusinessClass API response:', result);
      
      if (result && result.success && result.data) {
        setAllBusinessClasses(result.data);
        console.log('✅ BusinessClass мэдээлэл амжилттай авлаа:', result.data.length, 'ангилал');
      } else {
        console.warn('⚠️ BusinessClass API алдаа:', result?.message || 'Unknown error');
        setAllBusinessClasses([]);
      }
    } catch (error: any) {
      console.error('❌ Error fetching business classes:', error);
      setAllBusinessClasses([]);
    }
  }, []);

  // Fetch TemplateLedgerReceiptItem data
  const fetchReceiptItems = useCallback(async (ledgerTemplateReceiptId: string) => {
    try {
      setLoadingItems(true);
      
      const queryParams = new URLSearchParams({
        LedgerTemplateReceiptId: ledgerTemplateReceiptId
      });
      const apiUrl = `/api/template-ledger-receipt-item?${queryParams.toString()}`;
      
      const result = await api.get(apiUrl);
      
      if (result && result.success && result.data) {
        console.log('✅ Receipt items fetched:', result.data.length, 'items');
        setReceiptItems(result.data);
      } else {
        setErrorMessage(result.message || 'TemplateLedgerReceiptItem мэдээлэл авахад алдаа гарлаа');
        setShowErrorPopup(true);
        setReceiptItems([]);
      }
    } catch (error: any) {
      console.error('❌ Error fetching receipt items:', error);
      setErrorMessage(error.message || 'TemplateLedgerReceiptItem мэдээлэл авахад алдаа гарлаа');
      setShowErrorPopup(true);
      setReceiptItems([]);
    } finally {
      setLoadingItems(false);
    }
  }, []);

  // Fetch BusinessClass data immediately on mount
  useEffect(() => {
    console.log('🚀 TemplateLedgerPage mounted, fetching data...');
    
    // Fetch TemplateLedger data
    fetchTemplateLedger();
    
    // Fetch BusinessClass data - call directly to ensure it runs
    const loadBusinessClasses = async () => {
      console.log('🔍 Fetching BusinessClass data...');
      try {
        const result = await api.get('/api/BusinessClassOid');
        console.log('📡 BusinessClass API response:', result);
        
        if (result && result.success && result.data) {
          setAllBusinessClasses(result.data);
          console.log('✅ BusinessClass мэдээлэл амжилттай авлаа:', result.data.length, 'ангилал');
        } else {
          console.warn('⚠️ BusinessClass API алдаа:', result?.message || 'Unknown error');
          setAllBusinessClasses([]);
        }
      } catch (error: any) {
        console.error('❌ Error fetching business classes:', error);
        setAllBusinessClasses([]);
      }
    };
    
    loadBusinessClasses();
  }, []); // Only run once on mount

  // Get all BusinessClassName options from BusinessClass table
  const businessClassNameOptions = useMemo(() => {
    console.log('🔄 Computing businessClassNameOptions, allBusinessClasses:', allBusinessClasses.length);
    // Use all BusinessClass data instead of just from TemplateLedger
    const businessClassNames = allBusinessClasses
      .map(bc => bc.BusinessClassName || bc.Name)
      .filter(name => name !== null && name !== '')
      .sort((a, b) => a.localeCompare(b, 'mn-MN'));
    
    console.log('📋 BusinessClass options generated:', businessClassNames.length);
    return businessClassNames.map(name => ({
      value: name || '',
      label: name || ''
    }));
  }, [allBusinessClasses]);

  // Note filter options (Худалдан авалт, Борлуулалт)
  const noteOptions = useMemo(() => {
    return [
      { value: 'Худалдан авалт', label: 'Худалдан авалт' },
      { value: 'Борлуулалт', label: 'Борлуулалт' }
    ];
  }, []);

  // Handle BusinessClassName filter change
  const handleBusinessClassNameFilter = (value: string) => {
    setSelectedBusinessClassName(value);
  };

  // Handle Note filter change
  const handleNoteFilter = (value: string) => {
    setSelectedNote(value);
  };

  // Apply filters when either filter changes
  useEffect(() => {
    const applyFilters = async () => {
      console.log('🔍 Applying filters:', { selectedBusinessClassName, selectedNote });
      try {
        // Build query parameters
        const queryParams = new URLSearchParams();
        if (selectedBusinessClassName && selectedBusinessClassName !== '') {
          queryParams.append('BusinessClassName', selectedBusinessClassName);
        }
        if (selectedNote && selectedNote !== '') {
          queryParams.append('Note', selectedNote);
        }

        // If no filters selected, clear receipt items
        if (queryParams.toString() === '') {
          console.log('⚠️ No filters selected, clearing data');
          setReceiptItems([]);
          setSelectedTemplateLedgerOid(null);
          return;
        }

        // Fetch filtered TemplateLedger data from API
        const apiUrl = `/api/template-ledger?${queryParams.toString()}`;
        console.log('📡 Fetching filtered TemplateLedger:', apiUrl);
        const result = await api.get(apiUrl);
        
        console.log('📡 TemplateLedger filter response:', result);
        
        if (result && result.success && result.data) {
          const filteredData = result.data;
          console.log('✅ Filtered data received:', filteredData.length, 'items');
          
          // If filtered data exists, automatically fetch and show receipt items for the first item
          if (filteredData.length > 0) {
            const firstItemOid = filteredData[0].Oid;
            console.log('🔍 Filter applied - Auto-fetching receipt items for Oid:', firstItemOid);
            setSelectedTemplateLedgerOid(firstItemOid);
            await fetchReceiptItems(firstItemOid);
          } else {
            // No data found for this filter
            console.warn('⚠️ No TemplateLedger found for selected filters:', {
              BusinessClassName: selectedBusinessClassName,
              Note: selectedNote
            });
            console.warn('💡 Tip: Энэ BusinessClass болон Note-ийн хослолд TemplateLedger бүртгэгдээгүй байна. Эхлээд TemplateLedger үүсгэх шаардлагатай.');
            setReceiptItems([]);
            setSelectedTemplateLedgerOid(null);
          }
        } else {
          console.warn('⚠️ TemplateLedger API returned no data');
          setReceiptItems([]);
          setSelectedTemplateLedgerOid(null);
        }
      } catch (error: any) {
        console.error('❌ Error filtering template ledger:', error);
        setErrorMessage(error.message || 'TemplateLedger мэдээлэл шүүхэд алдаа гарлаа');
        setShowErrorPopup(true);
        setReceiptItems([]);
        setSelectedTemplateLedgerOid(null);
      }
    };

    applyFilters();
  }, [selectedBusinessClassName, selectedNote, fetchReceiptItems]);


  // TemplateLedgerReceiptItem columns
  const receiptItemColumns: SmartTableColumn[] = [
    {
      key: 'Oid',
      label: 'Oid',
      width: 280,
    },
    {
      key: 'Description',
      label: 'Тайлбар',
      width: 250,
    },
    {
      key: 'ToAmount',
      label: 'Дүн',
      width: 120,
      render: (value) => formatNumber(value),
    },
    {
      key: 'DebitAccountCode',
      label: 'Дт. Данс код',
      width: 120,
    },
    {
      key: 'DebitAccountName',
      label: 'Дт. Данс нэр',
      width: 200,
    },
    {
      key: 'CreditAccountCode',
      label: 'Кт. Данс код',
      width: 120,
    },
    {
      key: 'CreditAccountName',
      label: 'Кт. Данс нэр',
      width: 200,
    },
    {
      key: 'VATAccountCode',
      label: 'НӨАТ Данс код',
      width: 120,
    },
    {
      key: 'VATAccountName',
      label: 'НӨАТ Данс нэр',
      width: 200,
    },
    {
      key: 'CityTaxAccountCode',
      label: 'ХТ Данс код',
      width: 120,
    },
    {
      key: 'CityTaxAccountName',
      label: 'ХТ Данс нэр',
      width: 200,
    },
    {
      key: 'CashflowCategoryName',
      label: 'Мөнгөн урсгалын ангилал',
      width: 200,
    },
    {
      key: 'BusinessClassName',
      label: 'Бизнес ангилал',
      width: 200,
    },
  ];

  // Handle add new receipt item
  const handleAdd = useCallback(() => {
    if (!selectedTemplateLedgerOid) {
      if (!selectedBusinessClassName || !selectedNote) {
        setWarningMessage('Эхлээд Бизнес ангилал болон Тэмдэглэл сонгоно уу');
      } else {
        setWarningMessage(`"${selectedBusinessClassName}" болон "${selectedNote}" хослолд TemplateLedger олдсонгүй. Эхлээд TemplateLedger үүсгэх шаардлагатай.`);
      }
      setShowWarningAlert(true);
      return;
    }
    // Open popup for new item
    setSelectedItem(null);
    setShowPopup(true);
  }, [selectedTemplateLedgerOid]);

  // Handle edit receipt item - open popup
  const handleEdit = useCallback((row: TemplateLedgerReceiptItem) => {
    setSelectedItem(row);
    setShowPopup(true);
  }, []);

  // Handle popup success - refresh data
  const handlePopupSuccess = useCallback(() => {
    if (selectedTemplateLedgerOid) {
      fetchReceiptItems(selectedTemplateLedgerOid);
    }
    setShowPopup(false);
    setSelectedItem(null);
    setWarningMessage('Мэдээлэл амжилттай хадгалагдлаа');
    setShowWarningAlert(true);
  }, [selectedTemplateLedgerOid, fetchReceiptItems]);

  return (
    <LayoutWrapper userAuth={userAuth}>
      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-800">
            Гүйлгээний загварын дэлгэрэнгүй
          </h1>
        </div>

        <div className="mb-4 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
              Бизнес ангилал:
            </label>
            <SearchableDropdown
              options={businessClassNameOptions}
              value={selectedBusinessClassName}
              onChange={handleBusinessClassNameFilter}
              placeholder="Бүгдийг харуулах"
              className="min-w-[250px]"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
              Тэмдэглэл:
            </label>
            <SearchableDropdown
              options={noteOptions}
              value={selectedNote}
              onChange={handleNoteFilter}
              placeholder="Бүгдийг харуулах"
              className="min-w-[200px]"
            />
          </div>
        </div>

        <SmartTableButtons
          onRefresh={() => selectedTemplateLedgerOid && fetchReceiptItems(selectedTemplateLedgerOid)}
          onToggleSearch={() => setShowColumnSearch(!showColumnSearch)}
          onColumnSettings={() => setShowColumnSettings(!showColumnSettings)}
          showColumnSearch={showColumnSearch}
          addable={true}
          onAdd={handleAdd}
        />
        <SmartTable
          data={receiptItems}
          columns={receiptItemColumns}
          fetchData={() => selectedTemplateLedgerOid && fetchReceiptItems(selectedTemplateLedgerOid)}
          settingsKey="template-ledger-receipt-item-table"
          editable={false}
          addable={true}
          rowKey="Oid"
          showColumnSearch={showColumnSearch}
          showColumnSettings={showColumnSettings}
          onAdd={handleAdd}
          onEdit={handleEdit}
        />

        {showErrorPopup && (
          <ErrorPopup
            isVisible={showErrorPopup}
            message={errorMessage}
            onClose={() => setShowErrorPopup(false)}
          />
        )}

        {showWarningAlert && (
          <WarningPopup
            isVisible={showWarningAlert}
            message={warningMessage}
            onClose={() => setShowWarningAlert(false)}
          />
        )}

        <TemplateLedgerReceiptItemPopup
          isVisible={showPopup}
          onClose={() => {
            setShowPopup(false);
            setSelectedItem(null);
          }}
          onSuccess={handlePopupSuccess}
          itemData={selectedItem}
          ledgerTemplateReceiptId={selectedTemplateLedgerOid || undefined}
        />
      </div>
    </LayoutWrapper>
  );
};

export default TemplateLedgerPage;
